<template>
	<div>
		<v-card class="category-card elevation-0 d-flex flex-column pt-2">
			<div class="category-card__container">
				<div class="category-card__outer">
					<div class="d-flex category-card__list">
						<SponsorBadge
							v-if="category.sponsored_by"
							:text="sponsoredText"
							icon="star"
							top="0"
							right="0"
						/>
						<div
							class="d-flex justify-space-between px-3 py-3"
							v-bind:style="{
								marginTop: category.sponsored_by ? '30px' : 0,
							}"
						>
							<div
								@click="toggleToPost"
								class="d-flex category-card__title text-2-lines"
							>
								<div class="category-card__meta mr-2">
									{{ category.category_name }}
								</div>
							</div>
						</div>

						<v-divider />

						<div class="d-flex justify-space-between align-center py-3 px-3">
							<div class="category-card__icons-container">
								<div class="category-card__icons-container__icon-info">
									<Icon type="forum_community" :size="24" color="#9b9b9b" />
									<div class="category-card__counter-num">
										{{ userCounter }}
									</div>
								</div>
								<div class="category-card__icons-container__icon-info">
									<Icon type="messages" :size="24" color="#9b9b9b" />
									<div class="category-card__counter-num">
										{{ category.forum_count }}
									</div>
								</div>
								<div
									v-if="lastActiveForum"
									class="category-card__icons-container__icon-info"
								>
									<Icon type="clock" :size="24" color="#9b9b9b" />
									<div class="category-card__counter-num">
										{{ lastActiveForum }}
									</div>
								</div>
							</div>
							<div
								class="category-card__follow"
								:class="bookmarked && 'category-card__follow-active'"
								style="cursor: pointer"
								@click="addToBookmark"
							>
								{{ bookmarked ? 'Joined' : 'Join' }}
							</div>
						</div>
					</div>
				</div>
			</div>
		</v-card>
	</div>
</template>

<script>
import Vue from 'vue'
import VueScrollTo from 'vue-scrollto'
import { mapActions, mapGetters } from 'vuex'
import { calcDateAgo } from '@/utils/helpers'
import Dwh from '@/mixins/Dwh'
import linkifyThreadText from '@/mixins/linkifyThreadText'
import SponsorBadge from '@/components/SponsorBadge.vue'

Vue.use(VueScrollTo)

export default {
	name: 'CategoryCard',
	mixins: [Dwh, linkifyThreadText],
	components: {
		SponsorBadge,
	},

	props: {
		category: {
			type: Object,
			default: () => ({}),
		},
		currentUser: {
			type: Object,
			default: () => ({}),
		},
	},

	data() {
		return {
			bookmarked: null,
			userCounter: 0,
		}
	},

	computed: {
		...mapGetters('app', ['hasOtherProfileAccess']),
		sponsoredText() {
			return `Sponsored by ${this.category.sponsored_by}`
		},
		lastActiveForum() {
			return calcDateAgo(this.category.date_last_active || {})
		},
	},

	methods: {
		...mapActions('event', ['toggleBookmark']),
		toggleToPost() {
			this.$router.push({
				name: 'forums-by-category-id',
				params: { id: this.category.thread_category_id },
				query: {
					name: this.category.category_name,
				},
			})
			this.sendTrigger({
				category: 'Communities List',
				action: 'click_community_card',
				module: 'Forums / Communities',
				title: this.category.category_name,
				id1: this.category.thread_category_id,
			})
		},
		dwhJoinHandler(type) {
			let actionName = type ? 'click_join_forum_category' : 'click_unjoin_forum_category'
			this.sendTrigger({
				category: 'Communities List',
				action: actionName,
				module: 'Forums / Communities',
				title: this.category.category_name,
				id1: this.category.thread_category_id,
			})
		},
		async addToBookmark() {
			const newStatus = this.bookmarked ? 0 : 1
			this.dwhJoinHandler(newStatus)
			this.userCounter += newStatus ? 1 : -1
			await this.toggleBookmark({
				user_id: this.currentUser.user_id,
				bookmark_entity_type: this.category.bookmark.bookmark_entity_type,
				bookmark_status: newStatus,
				bookmark_entity_id: this.category.bookmark.bookmark_entity_id,
			})
			this.bookmarked = newStatus
		},
	},

	created() {
		if (this.category) {
			this.bookmarked = this.category.bookmark.is_bookmarked
			this.userCounter = this.category.forum_users_count
		}
	},
}
</script>

<style lang="scss">
.category-card {
	background: #f1f1f1 !important;
	&__icons-container {
		display: flex;
		align-items: center;
		gap: 16px;
		&__icon-info {
			display: flex;
			align-items: center;
			gap: 4px;
		}
	}
	&__title {
		font-family: 'Inter', sans-serif;
		font-weight: 700;
		font-size: 20px;
		line-height: 22px;
		letter-spacing: 0;
		color: #002855;
		text-decoration: unset;
		cursor: pointer;
		a {
			color: var(--link) !important;
		}
	}
	&__user-info-container {
		display: flex;
		flex-direction: column;
		justify-content: space-between;
	}

	&__container {
		width: 100%;
		height: 100%;
		border-radius: 8px !important;
		background: #ffffff !important;
	}

	&__counter {
		margin-right: 24px;
		@media (max-width: 480px) {
			margin: 0;
		}

		&-wrapper {
			display: flex;
			@media (max-width: 480px) {
				flex-direction: column;
				align-items: flex-end;
			}
		}

		&-num {
			font-family: 'Inter', sans-serif;
			font-weight: 700;
			font-size: 13px;
			line-height: 100%;
			letter-spacing: 0;
			color: #9b9b9b;
			text-align: right;
			display: flex;
			align-items: center;
		}
	}

	&__meta {
		display: -webkit-box;
		font-family: 'Inter', sans-serif;
		font-weight: 700;
		font-size: 20px;
		line-height: 22px;
		letter-spacing: 0;
		color: var(--primary_color);
		word-break: break-word;
		-webkit-line-clamp: 3;
		-webkit-box-orient: vertical;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	&__date {
		height: 40px;
		display: flex;
		align-items: flex-end;
		font-family: 'Inter', sans-serif;
		font-weight: 400;
		font-size: 13px;
		line-height: 100%;
		letter-spacing: 0;
		text-align: right;
		color: #9b9b9b;
	}

	&__author {
		font-family: 'Inter', sans-serif;
		font-weight: 700;
		font-size: 18px;
		line-height: 100%;
		letter-spacing: 0px;
		color: var(--username-category-title);
		word-break: break-word;
	}
	&__second-row {
		font-family: Inter, sans-serif;
		font-weight: 400;
		font-size: 15px;
		line-height: 20px;
		letter-spacing: 0;
		color: var(--company-position-large);
		word-break: break-word;
	}

	&__follow {
		height: 46px;
		width: 100px;
		background-color: var(--primary_color);
		display: flex;
		justify-content: center;
		align-items: center;
		font-family: 'Inter', sans-serif;
		font-weight: 700;
		font-size: 18px;
		line-height: 100%;
		letter-spacing: 0;
		text-align: center;
		color: #ffffff;
		border-radius: 8px;

		&-active {
			color: var(--secondary_color);
			background-color: #ffffff;
			border: 1.5px solid var(--secondary_color);
		}
	}

	&__avatar {
		border-radius: 8px;
		img {
			object-fit: cover;
		}
	}

	&__outer {
	}

	&__list {
		position: relative;
		flex-direction: column;
		border: 1px solid #e2e2e2;
		border-radius: 8px;
	}
	&__forums-title {
		padding: 12px;
		font-family: 'Inter', sans-serif;
		font-weight: 400;
		font-size: 16px;
		line-height: 140%;
		letter-spacing: 0;
		color: #4a4a4a;
		a {
			color: var(--link);
			text-decoration: none;
		}
	}

	@media screen and (max-width: 1904px) {
		&__list {
			flex: 0 0 75%;
		}
	}
	@media screen and (max-width: 1264px) {
		&__list {
			flex: 0 0 66%;
		}
	}

	@media screen and (min-width: 1264px) {
		&__container {
			max-width: 600px;
		}
	}
	@media screen and (max-width: 768px) {
		&__outer {
			flex-direction: column;
		}
	}
}
</style>
